import type { CSSProperties } from "react";

/** 空间首页：页面底色 */
export interface SpaceHomePageConfig {
  /** 首页最外层背景色（Hero 以下露边、滚动区域底色） */
  background: string;
}

/** 空间首页：粉色基本信息区 */
export interface SpaceHomeHeroConfig {
  /** 区域最小高度（px，不含 safe-area；0 表示随内容自适应） */
  height: number;
  /** Hero 容器顶部内边距（px，不含 safe-area） */
  paddingTop: number;
  /** 底部内边距（px） */
  paddingBottom: number;
  /** 左右内边距（px） */
  paddingX: number;
  /** 基本信息区（头像+昵称等）距 Hero 顶部的距离（px，不含 safe-area；叠加在 paddingTop 之下） */
  contentOffsetTop: number;
  /** 渐变背景起始色 */
  gradientFrom: string;
  /** 渐变背景结束色 */
  gradientTo: string;
}

/** 空间首页：白色底栏 */
export interface SpaceHomeSheetConfig {
  /** 顶部左右圆角（px） */
  borderRadiusTop: number;
  /** 上移与 Hero 重叠量（px），圆角需落在粉色 Hero 上才可见 */
  overlapHero: number;
  /** 背景色 */
  background: string;
}

/** 空间首页：头像 */
export interface SpaceHomeAvatarConfig {
  size: number;
  borderWidth: number;
  borderColor: string;
}

/** 空间首页：头像右下角关注按钮（抖音式加号 / 对勾） */
export interface SpaceHomeFollowBtnConfig {
  /** 圆形按钮直径（px） */
  size: number;
  /** 加号 / 对勾字号（px） */
  iconSize: number;
  /** 加号笔画粗细（px，仅未关注态 SVG 加号） */
  plusStrokeWidth: number;
  /** 角标距头像右边缘偏移（px，对应 CSS right，增大向左移） */
  offsetX: number;
  /** 角标距头像底边缘偏移（px，对应 CSS bottom，增大向上移） */
  offsetY: number;
  /** 未关注：圆形背景色（正红） */
  unfollowedBackground: string;
  /** 未关注：加号颜色 */
  unfollowedIconColor: string;
  /** 已关注：圆形背景色（白） */
  followedBackground: string;
  /** 已关注：对勾颜色（粉） */
  followedIconColor: string;
  /** 关注后对勾展示时长（ms），之后隐藏 */
  checkmarkDurationMs: number;
}

/** 空间首页：粉丝 / 订阅数据 */
export interface SpaceHomeMetricsConfig {
  /** 数值字号（px） */
  valueFontSize: number;
  /** 文案（粉丝/订阅）字号（px） */
  labelFontSize: number;
  /** 与龙粉号行的间距（px） */
  marginTop: number;
  /** 粉丝与订阅横向间距（px） */
  gap: number;
  /** 数值颜色 */
  valueColor: string;
  /** 文案（粉丝/订阅）颜色 */
  labelColor: string;
}

/** 空间首页：空间简介（标语） */
export interface SpaceHomeIntroConfig {
  /** 字号（px） */
  fontSize: number;
  /** 内容区顶部与标语的间距（px） */
  marginTop: number;
  /** 左侧缩进（px） */
  marginLeft: number;
}

/** 空间内二级页顶栏（个人资料 / 我的订单 / 我的权益等 H5SubPageShell） */
export interface SpaceHomeSubPageShellConfig {
  /** 顶栏高度（px） */
  headerHeight: number;
  /** 顶栏左右内边距（px） */
  headerPaddingX: number;
  /** 顶栏左右占位宽度（px），与返回区对称以保持标题居中 */
  headerSideWidth: number;
  /** 标题字号（px） */
  titleFontSize: number;
}

/** 我的订单列表：订单状态配色与卡片布局 */
export interface SpaceHomeOrderListConfig {
  /** 已支付 */
  statusPaid: string;
  /** 已取消 */
  statusCancelled: string;
  /** 待支付 */
  statusUnpaid: string;
  /** 列表页背景色 */
  pageBackground: string;
  /** 订单卡片背景色 */
  cardBackground: string;
  /** 卡片间距（px） */
  cardGap: number;
  /** 卡片内边距（px） */
  cardPadding: number;
  /** 产品封面边长（px） */
  posterSize: number;
  /** 封面占位背景色 */
  posterPlaceholderBackground: string;
  /** 支付按钮描边/文字色 */
  payButtonColor: string;
  /** 金额金融字体族 */
  amountFontFamily: string;
  /** 商品单价字号（px） */
  amountFontSize: number;
  /** 合计金额字号（px，略大于单价） */
  totalAmountFontSize: number;
}

/** 空间首页：顶部栏（右上角菜单） */
export interface SpaceHomeTopBarConfig {
  /** 顶部栏距屏幕顶部的垂直距离（px，不含 safe-area；safe-area 会叠加） */
  offsetTop: number;
  /** 按钮点击热区边长（px） */
  buttonSize: number;
  /** 图标字号（px，antd icon） */
  iconSize: number;
  /** 下拉菜单上下内边距（px） */
  menuPaddingY: number;
  /** 菜单项上下内边距（px），调小可让列表更紧凑 */
  menuItemPaddingY: number;
  /** 菜单项左右内边距（px） */
  menuItemPaddingX: number;
  /** 菜单项字号（px） */
  menuItemFontSize: number;
}

/** 空间首页：名称旁认证标签 */
export interface SpaceHomeCertLabelConfig {
  /** 标签整体高度（px） */
  height: number;
  /** 文案字号（px） */
  fontSize: number;
  /** V 认证图标边长（px） */
  iconSize: number;
}

/** 空间首页：操作按钮 */
export interface SpaceHomeActionButtonConfig {
  height: number;
  borderRadius: number;
  gap: number;
}

/** 空间首页：瀑布流模块间距 */
export interface SpaceHomeFeedConfig {
  /**
   * 各模块之间的统一间距（px）
   * 直播 / 金刚区 / 教程 / 最新观点 / 权益订阅 均使用此值
   */
  sectionGap: number;
  /** 模块标题与下方内容的间距（px） */
  sectionHintMarginBottom: number;
}

/** 空间首页：最新观点卡片 */
export interface SpaceHomeViewpointConfig {
  /** 观点标题 / 正文区域左侧内边距（px） */
  contentPaddingLeft: number;
}

/** 空间首页：金刚区入口图标 */
export interface SpaceHomeDiamondConfig {
  /** 入口图标边长（px），同时作用于图标容器与图片 */
  iconSize: number;
}

/** 空间首页：教程卡片（小红书式竖版） */
export interface SpaceHomeCourseCardConfig {
  /** 封面区高度占比（%） */
  coverHeightPercent: number;
  /** 标题/播放量区高度占比（%） */
  metaHeightPercent: number;
  /** 卡片圆角（px） */
  borderRadius: number;
  /** 卡片宽高比，CSS aspect-ratio 写法，如 "3 / 4" */
  aspectRatio: string;
  /** 网格列数 */
  columns: number;
  /** 网格横向间距（px） */
  gridGapX: number;
  /** 网格纵向间距（px） */
  gridGapY: number;
  /** 下半区内边距（px） */
  metaPadding: number;
  /** 卡片整体背景色 */
  cardBackground: string;
  /** 上半封面区占位背景色（图片加载前/无图时） */
  coverBackground: string;
  /** ★ 下半部分背景色：标题 + 播放量区域（对应 .space-home-course-meta） */
  metaBackground: string;
}

/**
 * 空间首页布局配置（改数值后刷新即可，样式通过 CSS 变量注入）
 */
export const SPACE_HOME_LAYOUT = {
  page: {
    background: "rgb(250, 72, 126)",
  },
  hero: {
    height: 130,
    paddingTop: 0,
    paddingBottom: 12,
    paddingX: 16,
    contentOffsetTop: 10,
    gradientFrom: "rgb(216, 86, 197)",
    gradientTo: "rgb(250, 72, 126)",
  },
  sheet: {
    borderRadiusTop: 16,
    overlapHero: 16,
    background: " #fef7fd",
  },
  avatar: {
    size: 90,
    borderWidth: 3,
    borderColor: "#fff",
  },
  followBtn: {
    size: 24,
    iconSize: 14,
    plusStrokeWidth: 2.2,
    offsetX: 4,
    offsetY: 4,
    unfollowedBackground: "#fe2c55",
    unfollowedIconColor: "#ffffff",
    followedBackground: "#ffffff",
    followedIconColor: "rgb(250, 72, 126)",
    checkmarkDurationMs: 3000,
  },
  /** 顶部栏：右上角菜单；改 offsetTop 调节距顶垂直距离 */
  topBar: {
    offsetTop: 18,
    buttonSize: 36,
    iconSize: 20,
    menuPaddingY: 2,
    menuItemPaddingY: 6,
    menuItemPaddingX: 16,
    menuItemFontSize: 15,
  },
  /** 粉丝 / 订阅：改 valueFontSize / labelFontSize */
  metrics: {
    valueFontSize: 16,
    labelFontSize: 14,
    marginTop: 10,
    gap: 28,
    valueColor: "rgb(255, 255, 255)",
    labelColor: "rgb(227, 227, 227)",
  },
  /** 空间简介（标语）：位于内容区顶部 */
  intro: {
    fontSize: 14,
    marginTop: 0,
    marginLeft: 10,
  },
  /** 空间二级页顶栏（返回按钮尺寸见 H5BackButtonConfig.ts） */
  subPageShell: {
    headerHeight: 48,
    headerPaddingX: 8,
    headerSideWidth: 56,
    titleFontSize: 16,
  },
  /** 我的订单列表（淘宝式卡片） */
  orderList: {
    statusPaid: "rgb(250, 72, 126)",
    statusCancelled: "#999999",
    statusUnpaid: "#fa8c16",
    pageBackground: "#f5f5f5",
    cardBackground: "#ffffff",
    cardGap: 10,
    cardPadding: 12,
    posterSize: 80,
    posterPlaceholderBackground: "rgba(22, 24, 35, 0.06)",
    payButtonColor: "#fa8c16",
    amountFontFamily:
      '"JetBrains Mono", "SFMono-Regular", ui-monospace, "Liberation Mono", Menlo, Consolas, monospace',
    amountFontSize: 14,
    totalAmountFontSize: 17,
  },
  /** 名称旁认证标签：改 height / fontSize / iconSize */
  certLabel: {
    height: 20,
    fontSize: 12,
    iconSize: 12,
  },
  actionButtons: {
    height: 38,
    borderRadius: 8,
    gap: 10,
  },
  /** 瀑布流模块间距：改 sectionGap 即可统一调节各区块距离 */
  feed: {
    sectionGap: 20,
    sectionHintMarginBottom: 8,
  },
  /** 最新观点：改 contentPaddingLeft 调节正文左缩进 */
  viewpoint: {
    contentPaddingLeft: 0,
  },
  /** 金刚区：改 iconSize 调节入口图标尺寸 */
  diamond: {
    iconSize: 36,
  },
  /**
   * 空间教程 · 小红书式竖版卡片
   * 样式变量注入见 spaceHomeLayoutCssVars() → SpaceHomeStyle.css
   */
  courseCard: {
    coverHeightPercent: 70,
    metaHeightPercent: 30,
    borderRadius: 8,
    aspectRatio: "3 / 4",
    columns: 2,
    gridGapX: 8,
    gridGapY: 12,
    metaPadding: 8,
    /** 整张卡片底色（通常与 sheet 一致） */
    cardBackground: "#fff",
    /** 上半封面占位色（无封面图 / 加载前） */
    coverBackground: "rgba(22, 24, 35, 0.06)",
    /** ★ 下半部分背景色：标题 + 播放量区域（.space-home-course-meta） */
    metaBackground: " #ffffff",
  },
} as const satisfies {
  page: SpaceHomePageConfig;
  hero: SpaceHomeHeroConfig;
  sheet: SpaceHomeSheetConfig;
  avatar: SpaceHomeAvatarConfig;
  followBtn: SpaceHomeFollowBtnConfig;
  metrics: SpaceHomeMetricsConfig;
  intro: SpaceHomeIntroConfig;
  topBar: SpaceHomeTopBarConfig;
  subPageShell: SpaceHomeSubPageShellConfig;
  orderList: SpaceHomeOrderListConfig;
  certLabel: SpaceHomeCertLabelConfig;
  actionButtons: SpaceHomeActionButtonConfig;
  feed: SpaceHomeFeedConfig;
  viewpoint: SpaceHomeViewpointConfig;
  diamond: SpaceHomeDiamondConfig;
  courseCard: SpaceHomeCourseCardConfig;
};

/** 空间二级页布局 CSS 变量（注入到 `.h5-sub-page`） */
export function spaceHomeSubPageShellCssVars(): CSSProperties {
  const { subPageShell } = SPACE_HOME_LAYOUT;
  return {
    ["--h5-sub-page-header-height" as string]: `${subPageShell.headerHeight}px`,
    ["--h5-sub-page-header-padding-x" as string]: `${subPageShell.headerPaddingX}px`,
    ["--h5-sub-page-header-side-width" as string]: `${subPageShell.headerSideWidth}px`,
    ["--h5-sub-page-title-font-size" as string]: `${subPageShell.titleFontSize}px`,
  };
}

/** 我的订单列表 CSS 变量（注入到 `.space-mine-orders`） */
export function spaceHomeOrderListCssVars(): CSSProperties {
  const { orderList } = SPACE_HOME_LAYOUT;
  return {
    ["--space-mine-order-status-paid" as string]: orderList.statusPaid,
    ["--space-mine-order-status-cancelled" as string]: orderList.statusCancelled,
    ["--space-mine-order-status-unpaid" as string]: orderList.statusUnpaid,
    ["--space-mine-order-page-bg" as string]: orderList.pageBackground,
    ["--space-mine-order-card-bg" as string]: orderList.cardBackground,
    ["--space-mine-order-card-gap" as string]: `${orderList.cardGap}px`,
    ["--space-mine-order-card-padding" as string]: `${orderList.cardPadding}px`,
    ["--space-mine-order-poster-size" as string]: `${orderList.posterSize}px`,
    ["--space-mine-order-poster-placeholder-bg" as string]: orderList.posterPlaceholderBackground,
    ["--space-mine-order-pay-btn-color" as string]: orderList.payButtonColor,
    ["--space-mine-order-amount-font-family" as string]: orderList.amountFontFamily,
    ["--space-mine-order-amount-font-size" as string]: `${orderList.amountFontSize}px`,
    ["--space-mine-order-total-amount-font-size" as string]: `${orderList.totalAmountFontSize}px`,
  };
}

/** 空间首页布局 CSS 变量（注入到 `.space-home-page`） */
export function spaceHomeLayoutCssVars(): CSSProperties {
  const { page, hero, sheet, avatar, followBtn, metrics, intro, topBar, certLabel, actionButtons, feed, viewpoint, diamond, courseCard } =
    SPACE_HOME_LAYOUT;
  const radius = sheet.borderRadiusTop;
  return {
    ["--space-home-page-bg" as string]: page.background,
    ["--space-home-hero-height" as string]: `${hero.height}px`,
    ["--space-home-hero-padding-top" as string]: `${hero.paddingTop}px`,
    ["--space-home-hero-padding-bottom" as string]: `${hero.paddingBottom}px`,
    ["--space-home-hero-padding-x" as string]: `${hero.paddingX}px`,
    ["--space-home-hero-content-offset-top" as string]: `${hero.contentOffsetTop}px`,
    ["--space-home-hero-gradient-from" as string]: hero.gradientFrom,
    ["--space-home-hero-gradient-to" as string]: hero.gradientTo,
    ["--space-home-sheet-radius-top" as string]: `${radius}px`,
    ["--space-home-sheet-overlap" as string]: `${sheet.overlapHero}px`,
    ["--space-home-sheet-bg" as string]: sheet.background,
    ["--space-home-avatar-size" as string]: `${avatar.size}px`,
    ["--space-home-follow-btn-size" as string]: `${followBtn.size}px`,
    ["--space-home-follow-btn-icon-size" as string]: `${followBtn.iconSize}px`,
    ["--space-home-follow-btn-plus-stroke-width" as string]: `${followBtn.plusStrokeWidth}px`,
    ["--space-home-follow-btn-offset-x" as string]: `${followBtn.offsetX}px`,
    ["--space-home-follow-btn-offset-y" as string]: `${followBtn.offsetY}px`,
    ["--space-home-follow-btn-unfollowed-bg" as string]: followBtn.unfollowedBackground,
    ["--space-home-follow-btn-unfollowed-icon-color" as string]: followBtn.unfollowedIconColor,
    ["--space-home-follow-btn-followed-bg" as string]: followBtn.followedBackground,
    ["--space-home-follow-btn-followed-icon-color" as string]: followBtn.followedIconColor,
    ["--space-home-metric-value-font-size" as string]: `${metrics.valueFontSize}px`,
    ["--space-home-metric-label-font-size" as string]: `${metrics.labelFontSize}px`,
    ["--space-home-metric-margin-top" as string]: `${metrics.marginTop}px`,
    ["--space-home-metric-gap" as string]: `${metrics.gap}px`,
    ["--space-home-metric-value-color" as string]: metrics.valueColor,
    ["--space-home-metric-label-color" as string]: metrics.labelColor,
    ["--space-home-intro-font-size" as string]: `${intro.fontSize}px`,
    ["--space-home-intro-margin-top" as string]: `${intro.marginTop}px`,
    ["--space-home-intro-margin-left" as string]: `${intro.marginLeft}px`,
    ["--space-home-top-bar-offset-top" as string]: `${topBar.offsetTop}px`,
    ["--space-home-top-btn-size" as string]: `${topBar.buttonSize}px`,
    ["--space-home-top-btn-icon-size" as string]: `${topBar.iconSize}px`,
    ["--space-home-top-menu-padding-y" as string]: `${topBar.menuPaddingY}px`,
    ["--space-home-top-menu-item-padding-y" as string]: `${topBar.menuItemPaddingY}px`,
    ["--space-home-top-menu-item-padding-x" as string]: `${topBar.menuItemPaddingX}px`,
    ["--space-home-top-menu-item-font-size" as string]: `${topBar.menuItemFontSize}px`,
    ["--space-home-cert-height" as string]: `${certLabel.height}px`,
    ["--space-home-cert-font-size" as string]: `${certLabel.fontSize}px`,
    ["--space-home-cert-icon-size" as string]: `${certLabel.iconSize}px`,
    ["--space-home-action-btn-height" as string]: `${actionButtons.height}px`,
    ["--space-home-action-btn-radius" as string]: `${actionButtons.borderRadius}px`,
    ["--space-home-action-btn-gap" as string]: `${actionButtons.gap}px`,
    ["--space-home-feed-section-gap" as string]: `${feed.sectionGap}px`,
    ["--space-home-feed-hint-margin-bottom" as string]: `${feed.sectionHintMarginBottom}px`,
    ["--space-home-viewpoint-content-padding-left" as string]: `${viewpoint.contentPaddingLeft}px`,
    ["--space-home-diamond-icon-size" as string]: `${diamond.iconSize}px`,
    ["--space-home-course-cover-height" as string]: `${courseCard.coverHeightPercent}%`,
    ["--space-home-course-meta-height" as string]: `${courseCard.metaHeightPercent}%`,
    ["--space-home-course-radius" as string]: `${courseCard.borderRadius}px`,
    ["--space-home-course-aspect-ratio" as string]: courseCard.aspectRatio,
    ["--space-home-course-columns" as string]: String(courseCard.columns),
    ["--space-home-course-grid-gap-x" as string]: `${courseCard.gridGapX}px`,
    ["--space-home-course-grid-gap-y" as string]: `${courseCard.gridGapY}px`,
    ["--space-home-course-meta-padding" as string]: `${courseCard.metaPadding}px`,
    ["--space-home-course-card-bg" as string]: courseCard.cardBackground,
    ["--space-home-course-cover-bg" as string]: courseCard.coverBackground,
    ["--space-home-course-meta-bg" as string]: courseCard.metaBackground,
  };
}

export const SPACE_HOME_AVATAR_CONFIG = SPACE_HOME_LAYOUT.avatar;

/** 空间首页数据本地缓存 */
export interface SpaceHomeCacheConfig {
  version: number;
  keyPrefix: string;
}

export const SPACE_HOME_CACHE = {
  version: 1,
  keyPrefix: "lf.spaceHome",
} as const satisfies SpaceHomeCacheConfig;
